import axios from 'axios'

/*
 * What the app does when the API stops accepting the session.
 *
 * Two different things can end a session, and they are not the same message:
 *
 *   401  the token is no longer good — it expired, or it was never valid
 *   402  the token is fine, but the subscription behind it has ended
 *
 * Before this, neither did anything. A scout whose plan had run out stayed on
 * screen with panels that quietly failed to load, and an expired token looked
 * like the app being broken rather than like being signed out.
 *
 * It is one interceptor rather than a check in every screen, because a screen
 * that forgets to check is exactly how this goes wrong again.
 */

const REASON_KEY = 'signedOutReason'

/* A dashboard fires several requests at once, so an expired session produces a
   burst of failures. Only the first one gets to end things; without this the
   page would try to redirect five times. */
let ending = false

function onLoginPage() {
  return String(window.location.hash || '').indexOf('/login') > -1
}

export function endSession(reason) {
  if (ending || onLoginPage()) return
  ending = true

  try {
    sessionStorage.clear()
    sessionStorage.setItem(REASON_KEY, reason)
  } catch (err) {
    /* Private mode, or storage disabled. Signing out still has to happen —
       the message is the part we can afford to lose. */
  }

  /* Reload rather than navigate: React still holds the signed-in state in
     context, and only a fresh start is certain to drop it. Once the storage is
     empty ProtectedRoute sends us to the sign-in page by itself.

     Deliberately no hash change first. Setting it navigated React Router to the
     sign-in page immediately, which read the message above and cleared it — and
     then the reload arrived and showed a second, silent sign-in page. */
  window.location.reload()
}

/* Read once and clear, so the message appears on the sign-in page it was meant
   for and not again on the next one. */
export function takeSignedOutReason() {
  try {
    const reason = sessionStorage.getItem(REASON_KEY)
    if (reason) sessionStorage.removeItem(REASON_KEY)
    return reason || ''
  } catch (err) {
    return ''
  }
}

/* The trial ended while the app was open. Record it and go to the plans;
   every other screen now redirects there until access is restored. */
let trialNoticed = false
export function markTrialExpired() {
  try { sessionStorage.setItem('AccessState', 'trial_expired') } catch (err) { /* ignore */ }
  if (trialNoticed) return
  trialNoticed = true
  if (String(window.location.hash || '').indexOf('/plans') === -1) window.location.hash = '#/plans'
}

export function installSessionGuard() {
  axios.interceptors.response.use(
    response => response,
    error => {
      const status = error && error.response ? error.response.status : 0
      const body = error && error.response ? error.response.data : null

      const code = body && body.code

      if (status === 401) {
        endSession('Your session has ended. Please sign in again.')
      } else if (status === 402 && code === 'TRIAL_EXPIRED') {
        /* A trial that ran out is not signed out: the person was
           evaluating, and the useful next page is the plans. The app is
           locked to that page from here (see ProtectedRoute). */
        markTrialExpired()
      } else if (status === 402) {
        endSession(
          body && body.msg
            ? body.msg + ' Renew it to carry on.'
            : 'Your subscription has ended. Renew it to carry on.'
        )
      }
      /* 403 is left to the screen that asked: the session is fine, only
         that action is not allowed — a trial saving a list, for example. */

      return Promise.reject(error)
    }
  )
}
